<template>
 
  <f7-block class="mkt-disabled"  v-if="!marketplaceEnabled">
    <f7-icon f7="cart_fill" size="48" color="#8e8e93" />
    <p class="mkt-disabled-text">
      {{ $t("marketplace.notEnabledForGroup") }}
    </p>
    <f7-button  class="content-size-button"  v-if="isAdmin" fill large raised @click="enableMarketplace">{{
      $t("marketplace.enableButton")
    }}</f7-button>
  </f7-block>

  <template v-else>

    <f7-searchbar
      :placeholder="$t('marketplace.search')"
      :value="searchText"
      @input="searchText = $event.target.value"
      @searchbar:clear="searchText = ''"
      clear-button
    />

    <f7-segmented strong round class="mkt-type-filter" >
      <f7-button round :active="activeType === 'ALL'" @click="activeType = 'ALL'">
        {{ $t("common.all") }}
      </f7-button>
      <f7-button
        :active="activeType === 'PRODUCT'"
        @click="activeType = 'PRODUCT'"
      >
        {{ $t("marketplace.products") }}
      </f7-button>
      <f7-button
        :active="activeType === 'SERVICE'"
        @click="activeType = 'SERVICE'"
      >
        {{ $t("marketplace.services") }}
      </f7-button>
    </f7-segmented>

    <f7-block v-if="canPublish" class="mkt-cta">
      <f7-button fill @click="goCreate">
        {{ $t("marketplace.sell") }}
      </f7-button>
    </f7-block>
    <f7-block v-else-if="!isAdmin" class="mkt-cta">
      <p class="mkt-cta-hint">
        {{ $t("marketplace.notAllowed") }}
      </p>
    </f7-block>

    <template v-if="isAdmin">
      <f7-block class="mkt-admin-actions">
        <f7-button
          outline
          small raised
          :class="{ 'mkt-admin-active': adminTab === 'PENDING' }"
          @click="adminTab = 'PENDING'"
        >
          {{
            $t("marketplace.pendingApprovals", { count: pendingApprovals })
          }}
        </f7-button>
        <f7-button
          outline
          small raised
          :class="{ 'mkt-admin-active': adminTab === 'PUBLISHED' }"
          @click="adminTab = 'PUBLISHED'"
        >
          {{ $t("marketplace.tabsPublished") }}
        </f7-button>
        <f7-button
          outline
          small raised
          :class="{ 'mkt-admin-active': adminTab === 'REMOVED' }"
          @click="adminTab = 'REMOVED'"
        >
          {{ $t("marketplace.tabsRemoved") }}
        </f7-button>
<f7-button
            outline
            small raised
            :class="{ 'mkt-admin-active': adminTab === 'SHOP' }"
            @click="adminTab = 'SHOP'"
          >
            {{ $t("marketplace.tabsShop") }}
        </f7-button>
<f7-button
            outline
            small raised
            :class="{ 'mkt-admin-active': adminTab === 'NEGOTIATIONS' }"
            @click="adminTab = 'NEGOTIATIONS'"
          >
            {{ $t("marketplace.tabsNegotiations") }}
            <span v-if="groupNegotiationsCount" class="mkt-admin-badge">{{ groupNegotiationsCount }}</span>
          </f7-button>
      </f7-block>

      <f7-block v-if="adminTab === 'PENDING'" class="mkt-list">
        <OfferCard
          v-for="o in pendingOffers"
          :key="o.id"
          :offer="o"
          show-status
        >
          <template #actions>
            <f7-button fill small raised color="green" @click="openApprove(o)">
              {{ $t("marketplace.approve") }}
            </f7-button>
            <f7-button outline small raised @click="openChanges(o)">
              {{ $t("marketplace.requestChanges") }}
            </f7-button>
            <f7-button outline small raised color="red" @click="openReject(o)">
              {{ $t("marketplace.reject") }}
            </f7-button>
          </template>
        </OfferCard>
        <p v-if="!pendingOffers.length" class="mkt-empty">
          {{ $t("marketplace.emptyPending") }}
        </p>
      </f7-block>

      <f7-block v-else-if="adminTab === 'PUBLISHED'" class="mkt-list">
        <OfferCard
          v-for="o in publishedOffers"
          :key="o.id"
          :offer="o"
          show-status
        >
          <template #actions>
            <f7-button
              v-if="o.featured"
              outline
              small
              raised
              @click="marketplaceStore.toggleFeatured(o.id, groupId)"
            >
              {{ $t("marketplace.unfeature") }}
            </f7-button>
            <f7-button
              v-else
              outline
              small
              raised
              @click="marketplaceStore.toggleFeatured(o.id, groupId)"
            >
              {{ $t("marketplace.feature") }}
            </f7-button>
          </template>
        </OfferCard>
        <p v-if="!publishedOffers.length" class="mkt-empty">
          {{ $t("marketplace.emptyPublished") }}
        </p>
      </f7-block>

      <f7-block v-else-if="adminTab === 'REMOVED'" class="mkt-list">
        <OfferCard
          v-for="o in removedOffers"
          :key="o.id"
          :offer="o"
          show-status
        >
          <template #actions>
            <f7-button
              v-if="o.groupId === groupId"
              outline
              small
              raised
              color="red"
              @click="deleteOffer(o)"
            >
              {{ $t("marketplace.delete") }}
            </f7-button>
          </template>
        </OfferCard>
        <p v-if="!removedOffers.length" class="mkt-empty">
          {{ $t("marketplace.emptyRemoved") }}
        </p>
      </f7-block>

      <f7-block v-else-if="adminTab === 'SHOP'" class="mkt-list no-margin no-padding">
        <div class="mkt-shop-config">
          <f7-block-title>{{ $t('marketplace.shopConfig') }}</f7-block-title>
          <p class="mkt-shop-hint">{{ $t('marketplace.shopConfigHint') }}</p>
          <f7-list inset strong>
            <f7-list-input
              :label="$t('marketplace.shopName')"
              type="text"
              :value="shopDraft?.name || ''"
              @input="shopDraft && (shopDraft.name = $event.target.value)"
              :placeholder="$t('marketplace.shopNamePh')"
            />
            <f7-list-input
              :label="$t('marketplace.shopDescription')"
              type="textarea"
              :value="shopDraft?.description || ''"
              @input="shopDraft && (shopDraft.description = $event.target.value)"
              :placeholder="$t('marketplace.shopDescriptionPh')"
              :rows="3"
            />
            <f7-list-input
              :label="$t('marketplace.shopLogoUrl')"
              type="text"
              :value="shopDraft?.logoUrl || ''"
              @input="shopDraft && (shopDraft.logoUrl = $event.target.value)"
              :placeholder="'https://...'"
            />
            <f7-list-input
              :label="$t('marketplace.shopBannerUrl')"
              type="text"
              :value="shopDraft?.bannerUrl || ''"
              @input="shopDraft && (shopDraft.bannerUrl = $event.target.value)"
              :placeholder="'https://...'"
            />
            <f7-list-item :title="$t('marketplace.shopEnabled')">
              <template #after>
                <f7-toggle v-model:checked="shopDraft.enabled" color="green" />
              </template>
            </f7-list-item>
          </f7-list>
          <f7-button fill large @click="saveShopConfig" :disabled="savingShop">
            {{ $t("marketplace.saveShop") }}
          </f7-button>
        </div>

        <f7-block class="mkt-products-manager">
          <f7-block-title>
            {{ $t('marketplace.groupProductsTitle') }}
            <f7-link small @click="startNewProduct">{{ $t('marketplace.addProduct') }}</f7-link>
          </f7-block-title>

          <f7-block v-if="productFormOpen" class="mkt-product-form">
            <f7-list inset strong no-hairlines>
              <f7-list-input
                :label="$t('marketplace.productTitle')"
                type="text"
                :value="productDraft.title"
                @input="productDraft.title = $event.target.value"
              />
              <f7-list-input
                :label="$t('marketplace.productDesc')"
                type="textarea"
                :value="productDraft.description"
                @input="productDraft.description = $event.target.value"
                :rows="2"
              />
              <f7-list-input
                :label="$t('marketplace.productPrice')"
                type="text"
                inputmode="numeric"
                :value="productDraft.price"
                @input="productDraft.price = $event.target.value.replace(/[^0-9.]/g, '')"
              />
            </f7-list>
            <div class="mkt-product-form-actions">
              <f7-button small outline raised @click="cancelProductForm">
                {{ $t("common.cancel") }}
              </f7-button>
              <f7-button small fill raised @click="saveProduct">
                {{ $t("marketplace.saveProduct") }}
              </f7-button>
            </div>
          </f7-block>

          <f7-list v-if="adminProducts.length" inset>
            <f7-list-item
              v-for="p in adminProducts"
              :key="p.id"
              :title="p.title"
              :subtitle="p.description"
              :after="formatAdminPrice(p)"
            >
              <template #media>
                <img class="listing-avatar" :src="adminCover(p)" :alt="p.title" />
              </template>
              <template #after="{ }">
                <span class="mkt-product-after">
                  <span class="mkt-product-price">{{ formatAdminPrice(p) }}</span>
                  <f7-link small @click="editProduct(p)">
                    <f7-icon f7="pencil" size="15" />
                  </f7-link>
                  <f7-link small color="red" @click="removeProduct(p)">
                    <f7-icon f7="trash" size="15" />
                  </f7-link>
                </span>
              </template>
            </f7-list-item>
          </f7-list>
          <p v-else class="mkt-empty">{{ $t("marketplace.noGroupProducts") }}</p>
        </f7-block>
      </f7-block>

      <f7-block v-else-if="adminTab === 'NEGOTIATIONS'" class="mkt-list">
        <NegotiationManager :group-id="groupId" />
      </f7-block>
    </template>

    <template v-else>
      <f7-block v-if="featuredOffers.length">
        <f7-block-title>{{ $t("marketplace.featured") }}</f7-block-title>
        <OfferCard
          v-for="o in featuredOffers"
          :key="o.id"
          :offer="o"
          compact
          @click="openDetail(o)"
        />
      </f7-block>

      <f7-block v-if="newOffers.length">
        <f7-block-title>{{ $t("marketplace.newThisWeek") }}</f7-block-title>
        <OfferCard
          v-for="o in newOffers"
          :key="o.id"
          :offer="o"
          compact
          @click="openDetail(o)"
        />
      </f7-block>

      <f7-block>
        <f7-block-title>{{ $t("marketplace.allOffers") }}</f7-block-title>
        <OfferCard
          v-for="o in filteredPublished"
          :key="o.id"
          :offer="o"
          compact
          @click="openDetail(o)"
        />
        <p v-if="!filteredPublished.length" class="mkt-empty">
          {{ $t("marketplace.emptyPublished") }}
        </p>
      </f7-block>

      <f7-block>
        <f7-block-title>{{
          $t("marketplace.tabsMyListings")
        }}</f7-block-title>
        <OfferCard
          v-for="o in myOffers"
          :key="o.id"
          :offer="o"
          compact
          show-status
          @click="openDetail(o)"
        >
          <template #actions>
            <f7-button outline small raised @click.stop="openShare(o)">
              {{ $t("marketplace.submitToGroup") }}
            </f7-button>
          </template>
        </OfferCard>
        <p v-if="!myOffers.length" class="mkt-empty">
          {{ $t("marketplace.emptyMyListings") }}
        </p>
      </f7-block>
    </template>
  </template>

  <AdaptiveSheet
    v-model:opened="detailOpen"
    :close-on-escape="true"
   :tablet-fullscreen="false"
    @closed="selected = null"
  >
    <f7-page v-if="selected">
      <f7-navbar :title="$t('marketplace.title')">
        <f7-nav-right
          ><f7-link sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right
        >
      </f7-navbar>
      <f7-block>
        <OfferCard
          :offer="selected"
          :show-status="isAdmin || isMine(selected)"
        />
<f7-button
            v-if="!isMine(selected) && selected.negotiable"
            large
            outline
            raised
            color="orange"
            class="mkt-contact-btn"
            @click="openNegSheet(selected)"
          >
            <f7-icon f7="tag_fill" size="16" />
            {{ $t("marketplace.negotiatePrice") }}
        </f7-button>
        <f7-button
          v-if="isMine(selected)"
          large
          outline
          raised
          class="mkt-contact-btn"
          @click="openShare(selected)"
        >
          <f7-icon f7="paperplane" size="16" />
          {{ $t("marketplace.submitToGroup") }}
        </f7-button>
        <f7-button
          large
          fill
          raised
          class="mkt-contact-btn"
          @click="contactSeller(selected)"
        >
          <f7-icon :f7="contactIcon(selected)" size="16" />
          {{ $t("marketplace.contactSeller") }}
        </f7-button>
      </f7-block>
    </f7-page>
  </AdaptiveSheet>

  <AdaptiveSheet
    v-model:opened="approveOpen"
    :close-on-escape="true"
   :tablet-fullscreen="false"
    @closed="approveTarget = null"
  >
    <f7-page>
      <f7-navbar :title="$t('marketplace.approveTitle')">
        <f7-nav-right
          ><f7-link sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right
        >
      </f7-navbar>
      <f7-list inset strong>
        <f7-list-item :title="$t('marketplace.approveShowMarketplace')">
          <template #media
            ><f7-checkbox :checked="true" :disabled="true"
          /></template>
        </f7-list-item>
        <f7-list-item :title="$t('marketplace.approveShareChat')">
          <template #media>
            <f7-checkbox
              :checked="approveOptions.shareChat"
              @change="approveOptions.shareChat = $event.target.checked"
            />
          </template>
        </f7-list-item>
        <f7-list-item :title="$t('marketplace.approveDigest')">
          <template #media>
            <f7-checkbox
              :checked="approveOptions.inDigest"
              @change="approveOptions.inDigest = $event.target.checked"
            />
          </template>
        </f7-list-item>
      </f7-list>
      <f7-block>
        <f7-button large fill raised @click="confirmApprove">
          {{ $t("marketplace.confirm") }}
        </f7-button>
      </f7-block>
    </f7-page>
  </AdaptiveSheet>

  <AdaptiveSheet
    v-model:opened="changesOpen"
    :close-on-escape="true"
    :tablet-fullscreen="false"
    @closed="
      changeTarget = null;
      changeText = '';
    "
  >
    <f7-page>
      <f7-navbar :title="$t('marketplace.requestChangesTitle')">
        <f7-nav-right
          ><f7-link sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right
        >
      </f7-navbar>
      <f7-list inset strong>
        <f7-list-input
          type="textarea"
          :label="$t('marketplace.requestChangesHint')"
          :value="changeText"
          @input="changeText = $event.target.value"
        />
      </f7-list>
      <f7-block>
        <f7-button large raised fill @click="confirmChanges">
          {{ $t("marketplace.send") }}
        </f7-button>
      </f7-block>
    </f7-page>
  </AdaptiveSheet>

  <AdaptiveSheet
    v-model:opened="rejectOpen"
    :close-on-escape="true"
    :tablet-fullscreen="false"
    @closed="
      rejectTarget = null;
      rejectText = '';
    "
  >
    <f7-page>
      <f7-navbar :title="$t('marketplace.rejectTitle')">
        <f7-nav-right
          ><f7-link sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link></f7-nav-right
        >
      </f7-navbar>
      <f7-list inset strong>
        <f7-list-input
          type="textarea"
          :label="$t('marketplace.rejectHint')"
          :value="rejectText"
          @input="rejectText = $event.target.value"
        />
      </f7-list>
      <f7-block>
        <f7-button large fill raised color="red" @click="confirmReject">
          {{ $t("marketplace.reject") }}
        </f7-button>
      </f7-block>
    </f7-page>
  </AdaptiveSheet>

  <AdaptiveSheet
    v-model:opened="settingsOpen"
    swipe-to-close
    :tablet-fullscreen="false"
    style="height: 80%;"
  >
    <f7-page-content>
      <div class="card-header">
      <f7-block-title class="mkt-sheet-title">{{
        $t("marketplace.settingsTitle")
      }}</f7-block-title>
          <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
        </div>
        <f7-block>
      <MarketplaceSettings v-model="settingsDraft" />
        <f7-button large fill @click="saveSettings">
          {{ $t("marketplace.confirm") }}
        </f7-button>
      </f7-block>
    </f7-page-content>
  </AdaptiveSheet>

  <AdaptiveSheet
    v-model:opened="shareOpen"
    swipe-to-close
    :tablet-fullscreen="false"
    @closed="shareTarget = null"
  >
    <f7-page-content>
      <div class="card-header">
      <f7-block-title class="mkt-sheet-title">{{
        $t("marketplace.submitToGroupTitle")
      }}</f7-block-title>
          <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
        </div>
      <p class="mkt-share-hint">{{ $t("marketplace.submitToGroupHint") }}</p>
      <f7-searchbar
        :placeholder="$t('marketplace.search')"
        :value="shareSearch"
        @input="shareSearch = $event.target.value"
        clear-button
      />
      <f7-list v-if="filteredShareGroups.length" inset>
        <f7-list-item
          v-for="g in filteredShareGroups"
          :key="g.id"
          :title="g.name"
          :subtitle="g.tag"
          link
          @click="confirmShare(g)"
        >
          <template #after>
            <f7-icon v-if="shareLoading" f7="hourglass" size="14" />
          </template>
        </f7-list-item>
      </f7-list>
      <p v-else class="mkt-empty">{{ $t("marketplace.noGroupsToShare") }}</p>
    </f7-page-content>
  </AdaptiveSheet>

  <PriceNegotiationSheet
    v-model:opened="negSheetOpen"
    :product-id="negTarget?.id || ''"
    :product-name="negTarget?.title || ''"
    :current-price="negTarget?.price || 0"
    :seller-id="negTarget?.sellerUserId || ''"
    :seller-name="negTarget?.sellerName || ''"
    :group-id="groupId"
    @offer-submitted="onOfferSubmitted"
  />
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import OfferCard from "./OfferCard.vue";
import MarketplaceSettings from "./MarketplaceSettings.vue";
import PriceNegotiationSheet from "./PriceNegotiationSheet.vue";
import NegotiationManager from "./NegotiationManager.vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import { useGroupsStore } from "../../js/pinia-stores/group.js";
import { useMembersStore } from "../../js/pinia-stores/members.js";
import { useKeycloakStore } from "../../js/pinia-stores/keycloak.js";
import {
  useMarketplaceStore,
  MARKETPLACE_DEFAULTS,
  GROUP_SHOP_DEFAULTS,
  kindIsProduct,
} from "../../js/pinia-stores/marketplace.js";
import { CONTACT_METHODS } from "../../constants/listing-options.js";
import i18n from "../../js/i18n.js";

const props = defineProps({ groupId: { type: String, required: true } });

const { t } = i18n.global;
const groupsStore = useGroupsStore();
const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();
const marketplaceStore = useMarketplaceStore();

const group = computed(() => groupsStore.activeGroup);
const currentUserId = computed(() => keycloakStore.subject || "");
const isAdmin = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === currentUserId.value,
  );
  return viewer?.role === "OWNER" || viewer?.role === "ADMIN";
});

const marketplaceEnabled = computed(() => {
  const mode = group.value?.marketplaceMode;
  // Backend stores marketplace state as ModeBehavior; "OFF"/unset = disabled
  return !!mode && mode !== "OFF";
});
const marketplaceSettings = computed(() => group.value?.marketplace || null);
const listingExpiryDays = computed(
  () => marketplaceSettings.value?.listingExpiryDays || 30,
);

const searchText = ref("");
const activeType = ref("ALL");
const adminTab = ref("PENDING");

const pendingOffers = computed(() => marketplaceStore.pending(props.groupId));
const publishedOffers = computed(() =>
  marketplaceStore.published(props.groupId),
);
const removedOffers = computed(() => marketplaceStore.removed(props.groupId));
const featuredOffers = computed(() =>
  marketplaceStore.featured(props.groupId).slice(0, 3),
);
const newOffers = computed(() =>
  marketplaceStore.newThisWeek(props.groupId).slice(0, 3),
);
const myOffers = computed(() =>
  marketplaceStore.mine(props.groupId, currentUserId.value),
);
const pendingApprovals = computed(() =>
  marketplaceStore.pendingCount(props.groupId),
);

const filteredPublished = computed(() => {
  const q = searchText.value.toLowerCase().trim();
  return publishedOffers.value.filter((o) => {
    if (activeType.value === "PRODUCT" && !kindIsProduct(o.kind)) return false;
    if (activeType.value === "SERVICE" && kindIsProduct(o.kind)) return false;
    if (!q) return true;
    return [o.title, o.description, o.category, o.sellerName].some(
      (v) => v && String(v).toLowerCase().includes(q),
    );
  });
});

const isMember = computed(() =>
  membersStore.members.some((m) => m.userId === currentUserId.value),
);

const canPublish = computed(() => {
  if (!marketplaceEnabled.value) return false;
  // External sellers are not supported by the backend yet
  if (!isMember.value) return false;
  const mode = group.value?.marketplaceMode;
  if (mode === "ADMIN_ONLY") return isAdmin.value;
  // MEMBER_OFFERS / OPEN_MARKETPLACE / FEATURED_PARTNERS: members can publish
  return true;
});

const detailOpen = ref(false);
const selected = ref(null);

const approveOpen = ref(false);
const approveTarget = ref(null);
const approveOptions = reactive({ shareChat: false, inDigest: false });

const changesOpen = ref(false);
const changeTarget = ref(null);
const changeText = ref("");

const rejectOpen = ref(false);
const rejectTarget = ref(null);
const rejectText = ref("");

const settingsOpen = ref(false);
const settingsDraft = ref(null);

const shopConfigOpen = ref(false);
const shopDraft = ref(
  JSON.parse(JSON.stringify(marketplaceStore.getGroupShop(props.groupId))),
);
const savingShop = ref(false);

const adminProducts = ref([]);
const productFormOpen = ref(false);
const productDraft = ref({ title: "", description: "", price: "" });
const productEditId = ref(null);

const negSheetOpen = ref(false);
const negTarget = ref(null);

const groupNegotiationsCount = computed(() =>
  marketplaceStore.negotiations.filter((n) => n.groupId === props.groupId && n.status === "PROPOSED").length
);

onMounted(async () => {
  await Promise.all([
    groupsStore.fetchGroupDetail(props.groupId).catch(() => {}),
    membersStore.fetchMembers(props.groupId).catch(() => {}),
  ]);
  marketplaceStore.refreshAll(props.groupId);
  if (isAdmin.value) {
    loadAdminProducts();
  }
});

function goCreate() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/marketplace/create`, {
    props: { groupId: props.groupId , group: group.value },
  });
}
function enableMarketplace() {
  groupsStore
    .updateMarketplaceMode(props.groupId, "MEMBER_OFFERS")
    .catch((e) => {
      f7.dialog.alert(
        e?.response?.data?.message || t("marketplace.activationFailed"),
      );
    });
}
function openDetail(o) {
  selected.value = o;
  detailOpen.value = true;
}
function isMine(o) {
  return o.sellerUserId === currentUserId.value;
}
function contactIcon(o) {
  const meta = CONTACT_METHODS.find((c) => c.code === o.contactMethod);
  return meta?.icon || "message_fill";
}
function contactSeller(o) {
  const method = o.contactMethod || "CHAT";
  const value = o.contactValue || "";
  if (method === "WHATSAPP" && value) {
    window.open(`https://wa.me/${value.replace(/\D/g, "")}`, "_blank");
  } else if (method === "PHONE" && value) {
    window.open(`tel:${value}`);
  } else {
    f7.dialog.alert(t("marketplace.contactChat"));
  }
}

function openApprove(o) {
  approveTarget.value = o;
  approveOptions.shareChat = false;
  approveOptions.inDigest = false;
  approveOpen.value = true;
}
function confirmApprove() {
  if (!approveTarget.value) return;
  marketplaceStore.approveOffer(
    approveTarget.value.id,
    props.groupId,
    { ...approveOptions },
    listingExpiryDays.value,
  );
  f7.toast.show({ text: t("marketplace.publishedNow"), closeTimeout: 1500 });
  approveOpen.value = false;
  approveTarget.value = null;
}
function openChanges(o) {
  changeTarget.value = o;
  changeText.value = "";
  changesOpen.value = true;
}
function confirmChanges() {
  if (!changeTarget.value) return;
  marketplaceStore.requestChanges(
    changeTarget.value.id,
    props.groupId,
    changeText.value.trim(),
  );
  f7.toast.show({ text: t("marketplace.sent"), closeTimeout: 1500 });
  changesOpen.value = false;
  changeTarget.value = null;
  changeText.value = "";
}
function openReject(o) {
  rejectTarget.value = o;
  rejectText.value = "";
  rejectOpen.value = true;
}
function confirmReject() {
  if (!rejectTarget.value) return;
  marketplaceStore.rejectOffer(
    rejectTarget.value.id,
    props.groupId,
    rejectText.value.trim(),
  );
  f7.toast.show({ text: t("marketplace.rejected"), closeTimeout: 1500 });
  rejectOpen.value = false;
  rejectTarget.value = null;
  rejectText.value = "";
}
function deleteOffer(o) {
  f7.dialog.confirm(
    o.title ? `${o.title} ?` : t("marketplace.deleteConfirm"),
    t("marketplace.delete"),
    () => {
      marketplaceStore.deleteOffer(o);
      f7.toast.show({ text: t("marketplace.deleted"), closeTimeout: 1500 });
    },
  );
}

const shareOpen = ref(false);
const shareTarget = ref(null);
const shareGroups = ref([]);
const shareSearch = ref("");
const shareLoading = ref(false);

const filteredShareGroups = computed(() => {
  const q = shareSearch.value.trim().toLowerCase();
  if (!q) return shareGroups.value;
  return shareGroups.value.filter(
    (g) =>
      (g.name || "").toLowerCase().includes(q) ||
      (g.tag || "").toLowerCase().includes(q),
  );
});

async function openShare(o) {
  shareTarget.value = o;
  shareSearch.value = "";
  shareGroups.value = [];
  shareOpen.value = true;
  try {
    if (!groupsStore.groups.length) await groupsStore.fetchGroups();
  } catch {
    // directory unavailable — empty picker
  }
  shareGroups.value = (groupsStore.groups || []).filter(
    (g) => g.id !== props.groupId && !marketplaceStore.subFor(o, g.id),
  );
}

async function fetchGroupSettings(id) {
  try {
    const settings = await groupsStore.fetchOtherGroup(id);
    return settings?.data || null;
  } catch {
    return null;
  }
}

async function fetchGroupMembers(id) {
  try {
    const { data } = await client.get(`groups/${id}/members`);
    return data;
  } catch {
    return [];
  }
}

async function confirmShare(group) {
  if (!shareTarget.value || shareLoading.value) return;
  shareLoading.value = true;
  try {
    const target = await fetchGroupSettings(group.id);
    if (!target) {
      f7.dialog.alert(t("marketplace.errorLoadingGroup"));
      return;
    }
    const mkt = target.marketplace || {};
    const mktEnabled =
      (target.features || []).includes("MARKETPLACE") || mkt.enabled === true;
    if (!mktEnabled) {
      f7.dialog.alert(t("marketplace.notEnabledForGroup"));
      return;
    }
    const members = await fetchGroupMembers(group.id);
    const isMember = members.some((m) => m.userId === currentUserId.value);
    const allowsExternal = mkt.allowExternalSellers === true;
    if (!isMember && !allowsExternal) {
      f7.dialog.alert(t("marketplace.notMemberAndNoExternal"));
      return;
    }
    const ok = marketplaceStore.submitToGroup(shareTarget.value.id, group.id);
    if (ok) {
      f7.toast.show({
        text: t("marketplace.submittedToGroup"),
        closeTimeout: 2000,
      });
      shareOpen.value = false;
      shareTarget.value = null;
    } else {
      f7.toast.show({
        text: t("marketplace.alreadySubmitted"),
        closeTimeout: 2000,
      });
    }
  } finally {
    shareLoading.value = false;
  }
}

function openSettings() {
  settingsDraft.value = JSON.parse(
    JSON.stringify({
      ...MARKETPLACE_DEFAULTS,
      ...(group.value?.marketplace || {}),
    }),
  );
  settingsOpen.value = true;
}
const MARKETPLACE_MODE_BY_PUBLISH = {
  ADMINS_ONLY: "ADMIN_ONLY",
  MEMBERS_APPROVAL: "MEMBER_OFFERS",
  APPROVED_SELLERS: "OPEN_MARKETPLACE",
};

async function saveSettings() {
  // Persist the publish policy as the group's marketplace mode; the other
  // draft settings stay local until the backend exposes them.
  try {
    await groupsStore.updateMarketplaceMode(
      props.groupId,
      MARKETPLACE_MODE_BY_PUBLISH[settingsDraft.value?.whoCanPublish] ||
        "MEMBER_OFFERS",
    );
    settingsOpen.value = false;
    f7.toast.show({
      text: t("marketplace.settingsTitle"),
      closeTimeout: 1200,
    });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || t("marketplace.activationFailed"),
    );
  }
}

function openShopConfig() {
  shopDraft.value = JSON.parse(
    JSON.stringify(marketplaceStore.getGroupShop(props.groupId) || {}),
  );
  shopConfigOpen.value = true;
}

async function saveShopConfig() {
  if (!shopDraft.value) return;
  savingShop.value = true;
  try {
    await marketplaceStore.saveGroupShop(props.groupId, { ...shopDraft.value });
    shopConfigOpen.value = false;
    f7.toast.show({ text: t("marketplace.shopSaved"), closeTimeout: 1500 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("marketplace.shopSaveError"),
      t("common.error"),
    );
  } finally {
    savingShop.value = false;
  }
}

async function loadAdminProducts() {
  adminProducts.value = await marketplaceStore.fetchGroupProducts(props.groupId);
}

function startNewProduct() {
  productDraft.value = { title: "", description: "", price: "" };
  productEditId.value = null;
  productFormOpen.value = true;
}

function editProduct(p) {
  productDraft.value = {
    title: p.title || "",
    description: p.description || "",
    price: p.price == null ? "" : String(p.price),
  };
  productEditId.value = p.id;
  productFormOpen.value = true;
}

function cancelProductForm() {
  productFormOpen.value = false;
  productEditId.value = null;
}

async function saveProduct() {
  const title = productDraft.value.title.trim();
  const price = Number(productDraft.value.price);
  if (!title) {
    f7.toast.show({ text: t("marketplace.formRequired"), closeTimeout: 2000 });
    return;
  }
  const payload = {
    listingType: "PRODUCT",
    fulfillmentType: "CONTACT_SELLER",
    title,
    description: productDraft.value.description.trim(),
    price: price > 0 ? price : null,
    currency: "XOF",
    location: "",
    contactUrl: "",
    expiresAt: null,
    isPartnerOffer: false,
    media: [],
  };
  try {
    if (productEditId.value) {
      await marketplaceStore.updateGroupProduct(
        props.groupId,
        productEditId.value,
        payload,
      );
    } else {
      await marketplaceStore.createGroupProduct(props.groupId, payload);
    }
    productFormOpen.value = false;
    productEditId.value = null;
    await loadAdminProducts();
    f7.toast.show({
      text: t("marketplace.productSaved"),
      closeTimeout: 1500,
    });
  } catch (e) {
    f7.dialog.alert(e?.message || t("marketplace.productSaveError"), t("common.error"));
  }
}

function removeProduct(p) {
  f7.dialog.confirm(
    t("marketplace.deleteProductConfirm", { title: p.title }),
    t("marketplace.delete"),
    async () => {
      try {
        await marketplaceStore.removeGroupProduct(props.groupId, p.id);
        await loadAdminProducts();
        f7.toast.show({ text: t("marketplace.deleted"), closeTimeout: 1500 });
      } catch (e) {
        f7.dialog.alert(e?.message || t("marketplace.productSaveError"), t("common.error"));
      }
    },
  );
}

function adminCover(p) {
  const m = p.media?.[0];
  return m?.thumbnailUrl || m?.url || "";
}

function formatAdminPrice(p) {
  if (p.price == null || p.price === "") return t("marketplace.priceOnRequest");
  return `${Number(p.price).toLocaleString()}${p.currency ? " " + p.currency : ""}`;
}

function toggleDelivery() {}

function openNegSheet(o) {
  negTarget.value = o;
  negSheetOpen.value = true;
}

function onOfferSubmitted() {
  negTarget.value = null;
}

defineExpose({ isAdmin, openSettings });
</script>

<style scoped>
.mkt-type-filter {
  margin: 4px 16px 0;
}
.mkt-cta {
  padding-top: 10px;
  padding-bottom: 6px;
}
.mkt-cta-hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 8px 0 0;
}
.mkt-admin-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 6px;
  padding-bottom: 2px;
}
.mkt-admin-actions .f7-button {
  margin: 0;
}
.mkt-admin-active {
  background: color-mix(in srgb, var(--f7-theme-color) 14%, transparent);
}
.mkt-list {
  padding-top: 4px;
}
.mkt-empty {
  text-align: center;
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 24px 0;
}
.mkt-disabled {
  text-align: center;
  padding: 48px 24px;
}
.mkt-disabled-text {
  font-size: 14px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 12px 0;
}
.mkt-contact-btn {
  margin-top: 12px;
}
.mkt-sheet-title {
  text-align: center;
}
.mkt-admin-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
  background: #ff3b30; color: #fff; font-size: 10px; font-weight: 600;
  margin-left: 4px;
}
.mkt-shop-config {
  padding: 8px 0;
}
.mkt-shop-config .mkt-shop-hint {
  font-size: 13px; color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0 0 8px;
}
.mkt-shop-checkboxes {
  display: flex; align-items: center; gap: 10px; font-size: 12px;
}
.mkt-shop-checkboxes f7-checkbox { margin-right: 2px; }
</style>
